import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useOrganization } from '../contexts/OrganizationContext';

const CREATE_CONTEXT = gql`
  mutation CreateContext($input: CreateContextInput!) {
    createContext(input: $input) {
      context {
        id
        name
        version
        organizationId
        projectId
      }
      message
    }
  }
`;

const GET_CONTEXTS = gql`
  query GetContexts {
    contexts {
      id
      name
      version
      type
      content
      tags
      created_at
    }
  }
`;

export function ContextForm() {
  const { currentOrganization, currentProject, projects } = useOrganization();
  const [name, setName] = useState('');
  const [type, setType] = useState('document');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');

  // Auto-select current project when it changes
  React.useEffect(() => {
    if (currentProject) {
      setSelectedProjectId(currentProject.id);
    }
  }, [currentProject]);

  const [createContext, { loading }] = useMutation(CREATE_CONTEXT, {
    refetchQueries: [{ query: GET_CONTEXTS }],
    onCompleted: () => {
      setName('');
      setContent('');
      setTags('');
      alert('Context created successfully!');
    },
    onError: (error) => {
      alert(`Error: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrganization) {
      alert('Please select an organization first');
      return;
    }

    createContext({
      variables: {
        input: {
          name,
          type,
          content,
          tags: tags.split(',').map(t => t.trim()).filter(Boolean),
          organizationId: currentOrganization.id,
          projectId: selectedProjectId || currentProject?.id || null,
        },
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 border rounded-lg">
      <h2 className="text-xl font-bold mb-4">Create Context</h2>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border rounded px-3 py-2"
          required
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Type</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="w-full border rounded px-3 py-2"
        >
          <option value="document">Document</option>
          <option value="prompt">Prompt</option>
          <option value="spec">Spec</option>
          <option value="example">Example</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Project {!selectedProjectId && <span className="text-gray-500">(Optional)</span>}
        </label>
        <select
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          className="w-full border rounded px-3 py-2"
        >
          <option value="">No project (organization-wide)</option>
          {projects.map((project: any) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
        {currentOrganization && projects.length === 0 && (
          <p className="text-xs text-gray-500 mt-1">
            No projects yet. Create one from the sidebar!
          </p>
        )}
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Content</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full border rounded px-3 py-2 h-32"
          required
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          className="w-full border rounded px-3 py-2"
          placeholder="marketing, brand, v2"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 disabled:opacity-50"
      >
        {loading ? 'Creating...' : 'Create Context'}
      </button>
    </form>
  );
}
